import React from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '../types/navigation';
import { Icon, IconName } from '../components/Icon';
import { colors, spacing, fontWeights, borderRadius } from '../theme';
import { PRODUCTS, Product, ProductTarget } from '../data/products';

type NavigationProp = StackNavigationProp<RootStackParamList, 'M03HomeScreen'>;

interface Activity {
  id: string;
  customerName: string;
  age: number;
  productId: string;
  timeAgo: string;
}

const RECENT_ACTIVITIES: Activity[] = [
  { id: 'a1', customerName: 'Lê Văn Thành', age: 32, productId: 'XPQ', timeAgo: '10 phút trước' },
  { id: 'a2', customerName: 'Nguyễn Thị Mai', age: 28, productId: 'XTL', timeAgo: '2 giờ trước' },
  { id: 'a3', customerName: 'Trần Quang Hải', age: 45, productId: 'XUM', timeAgo: 'Hôm qua' },
];

const PRODUCT_ICON: Record<ProductTarget, IconName> = {
  'life-protection': 'shield',
  'education': 'school',
  'retirement': 'elderly',
  'savings': 'savings',
};

const PRODUCT_ICON_COLOR: Record<ProductTarget, string> = {
  'life-protection': colors.primary,
  'education': colors.tertiary,
  'retirement': '#6F42C1',
  'savings': colors.secondary,
};

const PRODUCT_TINT: Record<Product['type'], { bg: string; accent: string }> = {
  'linked-general': { bg: '#FFF1F2', accent: '#E11D48' },
  'linked-unit': { bg: '#E6F0FA', accent: colors.primary },
};

export const M03HomeScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();

  const handleCreateNew = () => {
    navigation.navigate('M04ProductListScreen');
  };

  const handleProductTap = (product: Product) => {
    navigation.navigate('M05ProductDetailScreen', { productId: product.id });
  };

  return (
    <ScrollView contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>
      {/* Greeting */}
      <View style={styles.header}>
        <Text style={styles.welcomeText}>Bảng điều khiển, Anh Minh! 👋</Text>
        <Text style={styles.dateText}>Thứ Hai, 22/06/2026</Text>
      </View>

      {/* Stats Card */}
      <View style={styles.statsCard}>
        <View style={styles.statRow}>
          <Text style={styles.statLabel}>Lượt minh họa</Text>
          <View style={{ flex: 1 }} />
          <View style={styles.statTrend}>
            <Icon name="trending_up" size={14} color={colors.secondary} />
            <Text style={styles.statTrendText}>+12%</Text>
          </View>
        </View>
        <Text style={styles.statNumber}>128</Text>
        <Text style={styles.statSubLabel}>tháng này</Text>

        <View style={styles.divider} />

        <View style={styles.statRow}>
          <Text style={styles.statLabel}>KH đã tư vấn</Text>
          <View style={{ flex: 1 }} />
          <View style={styles.statTrend}>
            <Icon name="trending_up" size={14} color={colors.secondary} />
            <Text style={styles.statTrendText}>+5 mới</Text>
          </View>
        </View>
        <Text style={styles.statNumber}>45</Text>
        <Text style={styles.statSubLabel}>khách mới</Text>

        <View style={styles.divider} />

        <View style={styles.statRow}>
          <Text style={styles.statLabel}>Tỷ lệ chốt</Text>
          <View style={{ flex: 1 }} />
        </View>
        <Text style={styles.statNumber}>68%</Text>
        <View style={styles.progressTrack}>
          <View style={[styles.progressFill, { width: '68%' }]} />
        </View>
      </View>

      {/* Reminder */}
      <TouchableOpacity style={styles.reminderCard} activeOpacity={0.85}>
        <View style={styles.reminderIconWrap}>
          <Icon name="notifications" size={22} color={colors.primary} />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={styles.reminderTitle}>Lời nhắc hôm nay</Text>
          <Text style={styles.reminderText} numberOfLines={2}>
            Bạn có 3 lịch hẹn tư vấn cần chuẩn bị.
          </Text>
        </View>
        <Text style={styles.reminderAction}>Xem thêm</Text>
      </TouchableOpacity>

      {/* Primary CTA */}
      <TouchableOpacity style={styles.createBtn} onPress={handleCreateNew} activeOpacity={0.9}>
        <Icon name="add" size={22} color="#ffffff" />
        <Text style={styles.createBtnText}>Tạo minh họa mới</Text>
      </TouchableOpacity>

      {/* Popular Products Carousel */}
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Sản phẩm phổ biến</Text>
        <TouchableOpacity onPress={handleCreateNew}>
          <Text style={styles.seeAllText}>Xem tất cả →</Text>
        </TouchableOpacity>
      </View>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.popularRow}
      >
        {PRODUCTS.map((product) => {
          const tint = PRODUCT_TINT[product.type];
          return (
            <TouchableOpacity
              key={product.id}
              style={styles.productCard}
              activeOpacity={0.9}
              onPress={() => handleProductTap(product)}
            >
              <View style={[styles.productBanner, { backgroundColor: tint.accent }]}>
                <Icon name="shield" size={26} color="#ffffff" />
                {product.isDefault && (
                  <View style={styles.hotBadge}>
                    <Icon name="local_fire_department" size={10} color="#ffffff" filled />
                    <Text style={styles.hotBadgeText}>MẶC ĐỊNH</Text>
                  </View>
                )}
              </View>
              <View style={styles.productInfo}>
                <Text style={styles.productName} numberOfLines={1}>
                  {product.name}
                </Text>
                <Text style={styles.productDesc} numberOfLines={2}>
                  {product.tagline}
                </Text>
                <View style={styles.productTargetsRow}>
                  {product.targets.slice(0, 4).map((t) => (
                    <View
                      key={t}
                      style={[styles.targetDot, { backgroundColor: PRODUCT_ICON_COLOR[t] + '20' }]}
                    >
                      <Icon
                        name={PRODUCT_ICON[t]}
                        size={12}
                        color={PRODUCT_ICON_COLOR[t]}
                      />
                    </View>
                  ))}
                </View>
              </View>
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      {/* Recent Activities */}
      <Text style={styles.sectionTitleActivities}>Hoạt động gần đây</Text>
      <View style={styles.activitiesList}>
        {RECENT_ACTIVITIES.map((act, idx) => {
          const product = PRODUCTS.find((p) => p.id === act.productId);
          const tint = product ? PRODUCT_TINT[product.type] : { bg: '#f0f2f5', accent: colors.onSurfaceVariant };
          return (
            <TouchableOpacity
              key={act.id}
              style={[
                styles.activityItem,
                idx === RECENT_ACTIVITIES.length - 1 && { borderBottomWidth: 0 },
              ]}
              activeOpacity={0.85}
              onPress={() => product && navigation.navigate('M07ResultScreen', { illustrationId: act.id })}
            >
              <View style={[styles.activityIconWrap, { backgroundColor: tint.bg }]}>
                <Icon name="description" size={20} color={tint.accent} />
              </View>
              <View style={styles.activityInfo}>
                <Text style={styles.activityTitle} numberOfLines={1}>
                  {act.customerName}, {act.age} tuổi
                </Text>
                <Text style={styles.activitySub} numberOfLines={1}>
                  Sản phẩm: {product?.name ?? act.productId}
                </Text>
              </View>
              <Text style={styles.activityTime}>{act.timeAgo}</Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: spacing[4],
    paddingTop: spacing[4],
    paddingBottom: spacing[8],
    backgroundColor: '#f5f7fa',
    flexGrow: 1,
  },
  header: {
    marginBottom: spacing[4],
  },
  welcomeText: {
    fontSize: 22,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  dateText: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
    marginTop: spacing[1],
  },
  statsCard: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.lg,
    padding: spacing[4],
    marginBottom: spacing[4],
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 2,
  },
  statRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  statLabel: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
    fontWeight: fontWeights.medium,
  },
  statTrend: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  statTrendText: {
    fontSize: 12,
    fontWeight: fontWeights.bold,
    color: colors.secondary,
  },
  statNumber: {
    fontSize: 28,
    fontWeight: fontWeights.bold,
    color: colors.primary,
    marginTop: spacing[1],
    letterSpacing: -0.5,
  },
  statSubLabel: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
    marginTop: 2,
  },
  divider: {
    height: 1,
    backgroundColor: colors.outlineVariant,
    marginVertical: spacing[3],
  },
  progressTrack: {
    height: 6,
    backgroundColor: '#f0f2f5',
    borderRadius: 3,
    marginTop: spacing[2],
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: colors.secondary,
    borderRadius: 3,
  },
  reminderCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#EDF1F5',
    borderRadius: borderRadius.md,
    padding: spacing[3],
    marginBottom: spacing[4],
    gap: spacing[3],
  },
  reminderIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#ffffff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  reminderTitle: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  reminderText: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
    marginTop: 2,
    lineHeight: 16,
  },
  reminderAction: {
    fontSize: 12,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  createBtn: {
    backgroundColor: colors.primary,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing[4],
    borderRadius: borderRadius.md,
    gap: spacing[2],
    marginBottom: spacing[5],
    shadowColor: colors.primary,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 4,
  },
  createBtnText: {
    color: '#ffffff',
    fontSize: 15,
    fontWeight: fontWeights.bold,
    letterSpacing: 0.3,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: spacing[3],
  },
  sectionTitle: {
    fontSize: 17,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  seeAllText: {
    fontSize: 13,
    color: colors.secondary,
    fontWeight: fontWeights.semibold,
  },
  popularRow: {
    gap: spacing[3],
    paddingBottom: spacing[4],
    paddingRight: spacing[4],
  },
  productCard: {
    width: 168,
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 2,
  },
  productBanner: {
    height: 80,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  hotBadge: {
    position: 'absolute',
    top: spacing[2],
    right: spacing[2],
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 9999,
    backgroundColor: '#E11D48',
    gap: 2,
  },
  hotBadgeText: {
    fontSize: 8,
    fontWeight: '700',
    color: '#ffffff',
    letterSpacing: 0.3,
  },
  productInfo: {
    padding: spacing[3],
    gap: 4,
  },
  productName: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  productDesc: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
    lineHeight: 14,
  },
  productTargetsRow: {
    flexDirection: 'row',
    gap: 4,
    marginTop: spacing[1],
  },
  targetDot: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sectionTitleActivities: {
    fontSize: 17,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    marginTop: spacing[2],
    marginBottom: spacing[3],
  },
  activitiesList: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.lg,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 1,
  },
  activityItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing[4],
    gap: spacing[3],
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.outlineVariant,
  },
  activityIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  activityInfo: {
    flex: 1,
  },
  activityTitle: {
    fontSize: 14,
    fontWeight: fontWeights.semibold,
    color: colors.onSurface,
  },
  activitySub: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
    marginTop: 2,
  },
  activityTime: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
    fontWeight: fontWeights.medium,
  },
});
